import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Users, ShieldCheck, LogOut, Home, AlertCircle, GraduationCap, Menu, X, UserCircle, Crown, Search, Star } from "lucide-react";
import LSSDLogo from "../components/LSSDLogo";
import PageTransition from "../components/PageTransition";
import NotificationBell from "../components/NotificationBell";
import MDTFrame from "../components/MDTFrame";
import MDTBoot from "../components/MDTBoot";
import { useAuth } from "../context/AuthContext";
import { ExamModeProvider, useExamMode } from "../context/ExamModeContext";
import { getRankPatch } from "../data/rankPatches";

export default function DashboardLayout() {
  return (
    <ExamModeProvider>
      <DashboardLayoutInner />
    </ExamModeProvider>
  );
}

function DashboardLayoutInner() {
  const { user, logout } = useAuth();
  const { examMode } = useExamMode();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Helper passato ai link per chiudere il drawer mobile al click
  const closeDrawer = () => setMobileOpen(false);
  // Suppress unused warning su useLocation se non più riferito
  void location;

  // Blocca scroll body quando il drawer è aperto
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const links = [
    user && user.is_student && {
      to: "/dashboard/allievi",
      label: "Area Allievi",
      icon: GraduationCap,
      testId: "sidebar-allievi",
    },
    user && user.perm_agents && {
      to: "/dashboard/agenti",
      label: "Area Agenti",
      icon: BookOpen,
      testId: "sidebar-agenti",
    },
    user && (user.perm_hr || user.is_viewer) && {
      to: "/dashboard/hr",
      label: user.is_viewer ? "Risorse Umane (Solo lettura)" : "Risorse Umane",
      icon: Users,
      testId: "sidebar-hr",
    },
    user && user.perm_detective_bureau && {
      to: "/dashboard/detective-bureau",
      label: "Detective Bureau",
      icon: Search,
      testId: "sidebar-detective-bureau",
    },
    user && user.is_command && {
      to: "/dashboard/comando",
      label: "Area Comando",
      icon: ShieldCheck,
      testId: "sidebar-comando",
    },
    user && user.is_executive && {
      to: "/dashboard/esecutiva",
      label: "Area Esecutiva",
      icon: Crown,
      testId: "sidebar-esecutiva",
    },
    user && user.role === "Sceriffo" && {
      to: "/dashboard/sceriffo",
      label: "Sezione Sceriffo",
      icon: Star,
      testId: "sidebar-sceriffo",
    },
    user && {
      to: "/dashboard/profilo",
      label: "Il mio profilo",
      icon: UserCircle,
      testId: "sidebar-profilo",
    },
  ].filter(Boolean);

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <MDTFrame>
      <MDTBoot />
      <div className="min-h-full flex flex-col lg:flex-row" data-testid="dashboard-layout">
        {/* MOBILE TOPBAR — visibile solo su < lg */}
        {!examMode && (
          <div className="lg:hidden sticky top-0 z-40 bg-[#06140F]/95 backdrop-blur-xl border border-sheriff-gold/25 px-4 py-3 flex items-center justify-between gap-3 rounded-md mb-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <LSSDLogo className="h-8 w-8 shrink-0" />
              <div className="min-w-0">
                <div className="font-heading font-black text-base text-[#F5E6B8] leading-tight truncate">
                  {user?.rp_name || "LSSD"}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-sheriff-gold/80 truncate">
                  {user?.role || "Operatore"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {user && <NotificationBell />}
              <button
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-menu-open"
                aria-label="Apri menu"
                className="h-10 w-10 rounded-xl border border-sheriff-gold/40 bg-sheriff-gold/10 text-sheriff-gold flex items-center justify-center shrink-0 active:scale-95 transition-transform"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* MOBILE DRAWER OVERLAY */}
        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* SIDEBAR MDT — dentro alla cornice, restilizzata terminale */}
        <aside
          className={`
            mdt-sidebar
            lg:w-64 xl:w-72 bg-gradient-to-b from-[#06140F] to-[#0A1F18] text-white lg:h-full flex flex-col rounded-md border border-sheriff-gold/25
            fixed lg:static inset-y-0 left-0 z-50 w-[85%] max-w-xs lg:max-w-none transform transition-transform duration-300 lg:transform-none
            ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          `}
          data-testid="dashboard-sidebar"
        >
          {/* TOP MDT: Logo + Quick actions */}
          <div className="p-4 border-b border-sheriff-gold/20">
            <div className="mdt-panel-tag">▸ CMD/UNIT · IDENT</div>
            <div className="flex items-center gap-3 mb-4">
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-sheriff-gold/25 blur-lg rounded-full" />
                <LSSDLogo className="h-11 w-11 relative" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-black text-base text-[#F5E6B8] leading-tight">LSSD</div>
                <div className="uppercase-label text-[9px] text-sheriff-gold">Mobile Terminal</div>
              </div>
              {/* Close button - solo mobile */}
              <button
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Chiudi menu"
                className="lg:hidden h-9 w-9 rounded-xl border border-sheriff-gold/30 text-sheriff-gold/70 hover:text-sheriff-gold flex items-center justify-center active:scale-95 transition-transform"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/"
                data-testid="sidebar-home-link"
                onClick={closeDrawer}
                className="mdt-mini-btn mdt-mini-btn-gold"
              >
                <Home className="h-3 w-3" /> Home
              </Link>
              <button
                data-testid="sidebar-logout"
                onClick={handleLogout}
                className="mdt-mini-btn mdt-mini-btn-red"
              >
                <LogOut className="h-3 w-3" /> Esci
              </button>
            </div>
          </div>

          {/* Operator card */}
          {user && user !== false && (
            <div className="p-4 border-b border-sheriff-gold/20">
              <div className="mdt-panel-tag">▸ OPERATOR · PROFILE</div>
              <div className="bg-gradient-to-br from-[#143830] to-[#0F2620] border border-sheriff-gold/30 rounded-md p-3 relative overflow-hidden">
                <div className="mdt-corner-marks" aria-hidden />
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-sheriff-gold/15 border border-sheriff-gold/40 flex items-center justify-center shrink-0">
                    <span className="font-heading font-black text-sheriff-gold text-base">
                      {user.rp_name?.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase() || "OP"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="uppercase-label text-[#B8A269] text-[8px]">Codice</div>
                    <div className="font-semibold text-[#F5E6B8] truncate text-sm">{user.rp_name}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-dashed border-sheriff-gold/20">
                  <div className="text-[8px] uppercase tracking-[0.2em] font-bold text-[#B8A269] mb-2">Grado</div>
                  <div className="flex items-center gap-3">
                    {getRankPatch(user.role) && (() => {
                      const largeRanks = ["Sceriffo", "Sotto Sceriffo", "Assistente Sceriffo", "Capo Divisione"];
                      const isLarge = largeRanks.includes(user.role);
                      const size = isLarge ? "h-12 w-12" : "h-5 w-5";
                      return (
                        <div className="relative shrink-0">
                          {isLarge && <div className="absolute inset-0 bg-sheriff-gold/20 blur-lg rounded-full" />}
                          <img
                            src={getRankPatch(user.role)}
                            alt={user.role}
                            className={`relative ${size} object-contain drop-shadow-[0_0_4px_rgba(212,175,55,0.5)]`}
                            data-testid="sidebar-rank-patch"
                          />
                        </div>
                      );
                    })()}
                    <div className="font-mono text-sheriff-gold text-sm font-bold leading-tight" data-testid="sidebar-rank-name">
                      {user.role || "Senza ruolo"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto mdt-nav">
            <div className="mdt-panel-tag">▸ NAV · AREE OPERATIVE</div>
            {links.map(({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testId}
                onClick={closeDrawer}
                className={({ isActive }) =>
                  `mdt-nav-item ${isActive ? "mdt-nav-item-active" : ""}`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="mdt-nav-label">{label}</span>
                <span className="mdt-nav-arrow" aria-hidden>›</span>
              </NavLink>
            ))}
            {links.length === 0 && (
              <div className="px-4 py-3 text-sm text-[#8B7B49] flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Nessun permesso attivo. Contatta il Comando.</span>
              </div>
            )}
          </nav>

          {/* Footer info */}
          <div className="p-3 border-t border-sheriff-gold/20">
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-sheriff-gold/60 font-mono text-center">EST. 1852 · LSSD</div>
          </div>
        </aside>

        {/* MAIN — area contenuto scorrevole */}
        <main className="flex-1 min-w-0 lg:pl-4">
          {!examMode && (
            <div className="hidden lg:block bg-[#143830]/85 backdrop-blur-xl border border-sheriff-gold/25 rounded-md">
              <div className="px-6 sm:px-8 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono-codes text-xs text-sheriff-gold">PORTALE OPERATORI · LSSD</div>
                  <div className="font-heading text-2xl font-bold tracking-tight text-[#F5E6B8]">
                    Benvenuto, {user?.rp_name || "Operatore"}
                  </div>
                </div>
                {user && <NotificationBell />}
              </div>
            </div>
          )}
          <div className="p-4 sm:p-6 lg:p-8">
            <PageTransition variant="tight" />
          </div>
        </main>
      </div>
    </MDTFrame>
  );
}
